import { v4 as uuidv4 } from 'uuid';
import { 
  DistillOutput, 
  KeyPointJson, 
  ActionJson, 
  Task, 
  Takeaway,
  OwnerCandidate,
  UserField
} from '../types';

/**
 * Format owner candidates for display in the grid
 */
export const formatOwnerCandidates = (candidates: OwnerCandidate[]): string => {
  if (!candidates || candidates.length === 0) return '';
  
  return candidates
    .map(candidate => `${candidate.name} (${candidate.confidence.toFixed(1)})`)
    .join(', ');
};

/**
 * Convert KeyPointJson to Takeaway for UI
 * Preserves ID from backend (table storage RowKey) if available
 */
export const keyPointToTakeaway = (keyPoint: any): Takeaway => ({
  id: keyPoint.id || keyPoint.Id || uuidv4(), // Use backend ID (RowKey) if available, otherwise generate
  name: keyPoint.title || keyPoint.Title,
  type: keyPoint.ItemType || keyPoint.itemType, // ONLY ItemType - maps to decision/topic/request/etc.
  description: keyPoint.description || keyPoint.Description,
  confidence: keyPoint.confidence ?? keyPoint.Confidence ?? 0, // Use nullish coalescing to handle 0 values
  evidence: keyPoint.evidence || keyPoint.Evidence || [],
  tags: keyPoint.tags || keyPoint.Tags || [],
});

/**
 * Convert Takeaway back to KeyPointJson
 */
export const takeawayToKeyPoint = (takeaway: Takeaway): KeyPointJson => ({
  type: takeaway.type,
  title: takeaway.name,
  description: takeaway.description,
  evidence: takeaway.evidence,
  confidence: takeaway.confidence,
  // Note: tags are not part of KeyPointJson export format
});

/**
 * Convert ActionJson to Task for UI
 * Preserves ID from backend (table storage RowKey) if available
 */
export const actionToTask = (action: any): Task => {
  // Handle OwnerCandidates - format if it's an array, use as-is if it's already a string
  let ownerCandidatesFormatted = '';
  const candidates = action.ownerCandidates || action.OwnerCandidates || action.owner_candidates;
  
  if (typeof candidates === 'string') {
    // Already formatted (from table storage)
    ownerCandidatesFormatted = candidates;
  } else if (Array.isArray(candidates)) {
    // Raw array from backend - format it
    ownerCandidatesFormatted = formatOwnerCandidates(candidates);
  }

  // Parse owner and requester - they could be UserField objects or legacy strings
  const parseUserField = (field: any): UserField | null => {
    if (!field) return null;
    
    // If it's already a UserField object from backend (handle both camelCase, snake_case, and PascalCase)
    if (typeof field === 'object' && ('userId' in field || 'user_id' in field || 'UserId' in field)) {
      return {
        userId: field.userId || field.user_id || field.UserId || '',
        email: field.email || field.Email || '',
        name: field.name || field.Name || '',
        originalValue: field.originalValue || field.original_value || field.OriginalValue || ''
      };
    }
    
    // Legacy: if it's a string, create an unresolved UserField
    if (typeof field === 'string') {
      return {
        userId: '',
        email: '',
        name: '',
        originalValue: field
      };
    }
    
    return null;
  };

  const result = {
    id: action.id || action.Id || uuidv4(), // Use backend ID (RowKey) if available, otherwise generate
    meetingId: action.meetingId || action.MeetingId || action.partitionKey || action.PartitionKey, // PartitionKey from table storage
    title: action.title || action.Title,
    owner: parseUserField(action.owner || action.Owner),
    description: action.description || action.Description,
    ownerCandidates: ownerCandidatesFormatted,
    ownerCandidatesRaw: candidates, // Keep original array/object for backend
    requester: parseUserField(action.requester || action.Requester),
    due: action.due || action.Due,
    type: action.ItemType || action.itemType, // ONLY ItemType - maps to decision/topic/request/etc.
    status: 'Open', // Always default to "Open" (not from JSON)
    tags: action.tags || action.Tags || action.labels || [],
    priority: action.priority || action.Priority,
    confidence: action.confidence ?? action.Confidence ?? 0, // Use nullish coalescing to handle 0 values
    origin: 'native' as const,
    evidence: action.evidence || action.Evidence || [],
    // Salesforce tracking - task is locked after successful send
    isProcessed: action.isProcessed || action.IsProcessed || false,
    sentToSalesforceAt: action.sentToSalesforceAt || action.SentToSalesforceAt,
    sentToSalesforceBy: action.sentToSalesforceBy || action.SentToSalesforceBy,
  };
  
  return result;
};

/**
 * Convert Task back to ActionJson for API calls
 */
export const taskToAction = (task: Task): ActionJson => ({
  title: task.title,
  description: task.description,
  owner: task.owner?.name || task.owner?.originalValue || null,
  requester: task.requester?.name || task.requester?.originalValue || null,
  owner_candidates: [], // We don't edit candidates in UI, so preserve original or empty
  due: task.due,
  type: task.type,
  priority: task.priority,
  labels: task.tags,
  evidence: task.evidence,
  confidence: task.confidence,
  // Note: status and id not included in JSON export (UI-only fields)
});

/**
 * Convert Takeaway to Task (for conversion functionality)
 * Marks it as new so it will be created in backend, not updated
 */
export const convertTakeawayToTask = (takeaway: Takeaway): Task => ({
  id: uuidv4(), // Generate new temporary ID for the task
  title: takeaway.name,
  owner: null,
  description: takeaway.description,
  ownerCandidates: '',
  requester: null,
  due: null,
  type: takeaway.type || 'other', // Transfer type from takeaway, default to 'other' if missing
  status: 'Open', // Always "Open" for new tasks
  tags: takeaway.tags || [], // Preserve tags from takeaway
  priority: null,
  confidence: takeaway.confidence,
  origin: 'converted', // Will be set by store with originalId
  hiddenType: takeaway.type,
  evidence: takeaway.evidence,
  isNew: true, // Mark as new for backend creation
});

/**
 * Convert Task back to Takeaway (for revert functionality)
 * Only works for unsaved converted tasks
 */
export const revertTaskToTakeaway = (task: Task): Takeaway | null => {
  if (task.origin !== 'converted' || !task.originalId) {
    return null;
  }

  return {
    id: task.originalId,
    name: task.title,
    type: task.hiddenType || '',
    description: task.description,
    confidence: task.confidence,
    evidence: task.evidence,
    tags: task.tags || [],
  };
};

/**
 * Load data from DistillOutput JSON into UI state
 */
export const loadFromDistillOutput = (data: DistillOutput) => {
  const meetingTitle = data.meeting_brief.title;
  const executiveSummary = data.meeting_brief.executive_summary;
  const tasks = data.actions.map(actionToTask);
  const takeaways = data.key_points.map(keyPointToTakeaway);

  return {
    meetingTitle,
    executiveSummary,
    tasks,
    takeaways,
  };
};

/**
 * Export UI state back to DistillOutput JSON format
 * Note: UI only manages meeting_brief, key_points, and actions
 */
export const exportToDistillOutput = (
  meetingTitle: string,
  executiveSummary: string,
  tasks: Task[],
  takeaways: Takeaway[],
  originalData?: DistillOutput
): DistillOutput => {
  // Convert UI data back to JSON format
  const actions = tasks.map(taskToAction);
  const key_points = takeaways.map(takeawayToKeyPoint);

  // Build output structure - ONLY export sections managed by UI
  // themes, clarifications, and metrics are NOT exported (UI doesn't manage them)
  const result: DistillOutput = {
    meeting_brief: {
      title: meetingTitle,
      executive_summary: executiveSummary,
      detailed_summary: originalData?.meeting_brief.detailed_summary || '',
      participants: originalData?.meeting_brief.participants,
      main_themes: originalData?.meeting_brief.main_themes,
      next_steps: originalData?.meeting_brief.next_steps,
    },
    key_points,
    actions,
    // Empty arrays for RAG-generated sections (UI doesn't manage these)
    themes: [],
    clarifications: [],
    metrics: {
      total_key_points: key_points.length,
      total_actions: actions.length,
      total_themes: 0,
      confidence_score_avg: calculateAverageConfidence([...key_points, ...actions]),
    },
  };

  return result;
};

/**
 * Calculate average confidence score
 */
const calculateAverageConfidence = (items: { confidence: number }[]): number => {
  if (items.length === 0) return 0;
  
  const sum = items.reduce((acc, item) => acc + item.confidence, 0);
  return sum / items.length;
};

/**
 * Duplicate a task with a new ID
 * Marks it as new so it will be created in backend, not updated
 * Clears OriginalValue from owner/requester since this is a copy, not from file import
 */
export const duplicateTask = (task: Task): Task => {
  // Helper to clear OriginalValue from UserField
  const clearOriginalValue = (userField: UserField | null): UserField | null => {
    if (!userField) return null;
    return {
      ...userField,
      originalValue: '' // Clear OriginalValue for copied tasks
    };
  };

  return {
  ...task,
  id: uuidv4(), // Generate new temporary ID
  title: `${task.title} (Copy)`,
    owner: clearOriginalValue(task.owner),
    requester: clearOriginalValue(task.requester),
  origin: 'copy',
  originalId: undefined,
  hiddenType: undefined,
  isNew: true, // Mark as new for backend creation
  };
};
